import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

// Departments as configured master data (GET /departments), replacing what
// used to be four separately-maintained hardcoded rosters that had drifted
// out of sync with each other.

export interface DepartmentOption {
  id: string
  name: string
  color?: string
  description?: string
}

export function useDepartments() {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['departments', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/departments', { organizationId: organization?.id })
      const rows = res?.data || []
      return rows.map((r: any) => ({ ...r, id: r.id ?? r._id })) as DepartmentOption[]
    },
    enabled: !!organization?.id && can('PEOPLE', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useDepartmentMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['departments', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: { name: string; description?: string; color?: string; parentId?: string }) =>
      api.post<any>('/departments', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { name?: string; description?: string | null; color?: string; parentId?: string | null } }) =>
      api.patch<any>(`/departments/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/departments/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, remove }
}
